import { useState, useRef, useEffect } from 'react'
import { Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DateRange {
  from: string // YYYY-MM-DD
  to: string
}

export interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  presets?: Array<{ label: string; days: number }>
}

const DEFAULT_PRESETS = [
  { label: '7 Tage', days: 7 },
  { label: '14 Tage', days: 14 },
  { label: '30 Tage', days: 30 },
  { label: '60 Tage', days: 60 },
  { label: '90 Tage', days: 90 },
]

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function DateRangePicker({ value, onChange, presets = DEFAULT_PRESETS }: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function applyPreset(days: number) {
    onChange({ from: isoDaysAgo(days), to: isoToday() })
    setOpen(false)
  }

  const label = `${value.from} → ${value.to}`

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm hover:bg-[var(--muted)]"
      >
        <Calendar className="h-4 w-4" />
        <span className="tabular-nums">{label}</span>
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-md border border-[var(--border)] bg-[var(--background)] p-3 shadow-lg">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p.days)}
                className={cn(
                  'rounded-md border px-2 py-1 text-xs',
                  'border-[var(--border)] bg-[var(--background)] hover:bg-[var(--muted)]',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <label className="block text-xs text-[var(--muted-foreground)]">
              Von
              <input
                type="date"
                value={value.from}
                onChange={(e) => onChange({ ...value, from: e.target.value })}
                className="mt-1 h-8 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
              />
            </label>
            <label className="block text-xs text-[var(--muted-foreground)]">
              Bis
              <input
                type="date"
                value={value.to}
                onChange={(e) => onChange({ ...value, to: e.target.value })}
                className="mt-1 h-8 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  )
}
